import React, { useState } from 'react';
import { Gift, X, Check, Mail, User, Heart, ArrowRight, Sparkles, CreditCard, Lock, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import AlertModal from './AlertModal.js';
import { useAlert } from '../hooks/UseAlert.js';

// Clé publique Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// COMPOSANT FORMULAIRE DE PAIEMENT
const GiftCardPaymentForm = ({ selectedCard, formData, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { alertConfig, closeAlert, warning, success } = useAlert();

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '12px'
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Créer le Payment Intent côté serveur
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: selectedCard.value,
          paymentType: 'full', // Cartes cadeaux = paiement intégral uniquement
          bookingDetails: {
            userName: formData.senderName,
            userEmail: formData.senderEmail,
            serviceName: `Carte Cadeau - ${selectedCard.name}`,
            type: 'gift_card'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création du paiement');
      }

      const { clientSecret, paymentIntentId } = await response.json();

      if (!clientSecret) {
        throw new Error('Client secret manquant dans la réponse');
      }

      // Confirmer le paiement
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.senderName,
            email: formData.senderEmail,
            phone: formData.senderPhone
          }
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
        // Paiement réussi - enregistrer dans Firestore
        await addDoc(collection(db, 'giftcards'), {
          cardName: selectedCard.name,
          cardValue: selectedCard.value,
          senderName: formData.senderName,
          senderEmail: formData.senderEmail,
          senderPhone: formData.senderPhone,
          recipientName: formData.recipientName,
          recipientEmail: formData.recipientEmail,
          personalMessage: formData.personalMessage,
          paymentIntentId: paymentIntentId,
          paymentStatus: paymentIntent.status === 'succeeded' ? 'captured' : 'authorized',
          amountPaid: selectedCard.value,
          createdAt: new Date().toISOString(),
          validUntil: new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000).toISOString(), // 6 mois
          status: 'active',
          used: false
        });

        // Succès - passer les infos
        onSuccess(paymentIntentId);
      }
    } catch (err) {
      console.error('Erreur paiement carte cadeau:', err);
      setError(err.message || 'Une erreur est survenue lors du paiement. Veuillez réessayer.');
      setLoading(false);
    }
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section Paiement */}
        <div>
          <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Informations de paiement
          </h3>

          {/* Montant à payer */}
          <div className="bg-purple-50 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Montant à payer :</span>
              <span className="text-2xl font-bold text-purple-700">{selectedCard.value}€</span>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              ✓ Paiement sécurisé par Stripe • Validité 6 mois
            </p>
          </div>

          {/* Carte bancaire */}
          <div className="border border-gray-300 rounded-lg p-4 bg-white">
            <CardElement options={cardElementOptions} />
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Info importante */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Après le paiement :</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Vous recevrez un email de confirmation à <strong>{formData.senderEmail}</strong></li>
                <li>Le destinataire recevra la carte cadeau par email à <strong>{formData.recipientEmail}</strong></li>
                <li>La carte cadeau est valable 6 mois à partir d'aujourd'hui</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Badge sécurité */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <Lock className="w-4 h-4" />
          <span>Paiement 100% sécurisé par Stripe</span>
        </div>

        {/* Boutons */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!stripe || loading}
            className={`flex-1 bg-gradient-to-r ${selectedCard.gradient} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity ${loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {loading ? 'Paiement en cours...' : `Payer ${selectedCard.value}€`}
          </button>
        </div>
      </form>
    </>
  );
};

// COMPOSANT PRINCIPAL
const GiftCardSystem = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: infos, 2: paiement
  const [formData, setFormData] = useState({
    senderName: '',
    senderEmail: '',
    senderPhone: '',
    recipientName: '',
    recipientEmail: '',
    personalMessage: ''
  });

  // Ajout du hook useAlert pour accéder à success
  const { alertConfig, closeAlert, warning, success } = useAlert();

  const giftCards = [
    {
      id: 1,
      name: "Évasion Douceur",
      value: 50,
      gradient: "from-pink-400 to-rose-500",
      icon: <Heart className="w-8 h-8" />,
      options: [
        "1 Gommage corps (35€)",
        "1 Sérénité du dos 30min (40€)",
        "Crédit de 50€ pour tout soin au choix"
      ]
    },
    {
      id: 2,
      name: "Rituel Harmonie",
      value: 80,
      gradient: "from-purple-400 to-pink-500",
      icon: <Sparkles className="w-8 h-8" />,
      options: [
        "1 Soin visage Sérénité lumineuse (79€)",
        "1 Massage 1h au choix (90€) - crédit restant",
        "Crédit de 80€ pour composer votre soin"
      ]
    },
    {
      id: 3,
      name: "Voyage Sensoriel",
      value: 100,
      gradient: "from-purple-500 to-indigo-500",
      icon: <Gift className="w-8 h-8" />,
      options: [
        "1 Massage 1h au choix (90€)",
        "1 Rituel cristallin visage 1h15 (95€)",
        "Gommage + Massage corps 1h (69€) + Soin visage 30min (39€)"
      ]
    },
    {
      id: 4,
      name: "Collection Prestige",
      value: 130,
      gradient: "from-indigo-500 to-purple-600",
      icon: <Sparkles className="w-8 h-8" />,
      options: [
        "1 Massage 1h30 (120€)",
        "1 Rituel cristallin 1h45 (130€)",
        "1 Secret japonais sublimé 1h30 (110€)"
      ]
    },
    {
      id: 5,
      name: "Odyssée Bien-être",
      value: 160,
      gradient: "from-amber-400 to-orange-500",
      icon: <Heart className="w-8 h-8" />,
      options: [
        "2 Massages 1h au choix (180€) avec crédit restant",
        "1 Massage 1h30 (120€) + 1 Soin visage 45min (55€)",
        "Crédit libre de 160€ à utiliser selon vos envies"
      ]
    },
    {
      id: 6,
      name: "Symphonie Cristal",
      value: 200,
      gradient: "from-purple-600 to-pink-600",
      icon: <Gift className="w-8 h-8" />,
      options: [
        "2 Massages 1h30 au choix (240€) avec crédit",
        "1 Rituel cristallin 1h45 (130€) + 1 Massage 1h (90€)",
        "Forfait libre de 200€ pour créer votre parcours sur-mesure"
      ]
    }
  ];

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowForm(true);
    setCurrentStep(1);
  };

  const validateStep1 = () => {
    if (!formData.senderName || !formData.senderEmail || !formData.senderPhone ||
      !formData.recipientName || !formData.recipientEmail) {
      warning('⚠️ Veuillez remplir tous les champs obligatoires');
      return false;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.senderEmail) || !emailRegex.test(formData.recipientEmail)) {
      warning('⚠️ Veuillez entrer des adresses email valides');
      return false;
    }

    return true;
  };

  const handleContinueToPayment = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    // Envoyer l'email de confirmation (à implémenter côté serveur)
    try {
      await fetch('/api/send-giftcard-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderName: formData.senderName,
          senderEmail: formData.senderEmail,
          recipientName: formData.recipientName,
          recipientEmail: formData.recipientEmail,
          cardName: selectedCard.name,
          cardValue: selectedCard.value,
          personalMessage: formData.personalMessage,
          paymentIntentId: paymentIntentId
        })
      });
    } catch (error) {
      console.error('Erreur envoi email:', error);
    }

    // Afficher message de succès
    success(`Paiement réussi !\n\nCarte cadeau "${selectedCard.name}" de ${selectedCard.value}€\n\n📧 Emails de confirmation envoyés à :\n- Vous : ${formData.senderEmail}\n- Destinataire : ${formData.recipientEmail}\n\n🎁 La carte cadeau est valable 6 mois.\n\nMerci pour votre achat ! ✨`);

    // Reset
    setShowForm(false);
    setSelectedCard(null);
    setCurrentStep(1);
    setFormData({
      senderName: '',
      senderEmail: '',
      senderPhone: '',
      recipientName: '',
      recipientEmail: '',
      personalMessage: ''
    });
  };

  const handleCancel = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      setShowForm(false);
      setSelectedCard(null);
      setCurrentStep(1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
      {/* Section principale */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-purple-900 mb-4">Nos Cartes Cadeaux</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choisissez la carte cadeau parfaite et découvrez toutes les possibilités de soins incluses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {giftCards.map((card) => (
              <div
                key={card.id}
                onClick={() => handleCardClick(card)}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer overflow-hidden"
              >
                {/* Badge valeur */}
                <div className="absolute top-4 right-4">
                  <div className={`bg-gradient-to-r ${card.gradient} text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg`}>
                    {card.value}€
                  </div>
                </div>

                {/* Effet de fond */}
                <div className={`absolute top-0 left-0 w-full h-32 bg-gradient-to-br ${card.gradient} opacity-10 rounded-t-2xl`}></div>

                {/* Icône */}
                <div className={`relative w-16 h-16 bg-gradient-to-br ${card.gradient} rounded-xl flex items-center justify-center text-white mb-6 shadow-lg group-hover:rotate-12 transition-transform duration-300`}>
                  {card.icon}
                </div>

                {/* Titre */}
                <h3 className="text-2xl font-light text-purple-900 mb-4 p-1">{card.name}</h3>

                {/* Options */}
                <div className="space-y-2 mb-6">
                  <p className="text-sm font-semibold text-purple-700 mb-3">Exemples de soins possibles :</p>
                  {card.options.map((option, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{option}</span>
                    </div>
                  ))}
                </div>

                {/* Bouton */}
                <div className="flex items-center justify-between text-purple-600 font-medium group-hover:gap-3 transition-all">
                  <span>Commander</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Informations pratiques */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-purple-900 mb-4">Comment ça marche ?</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Choisissez</h3>
              <p className="text-gray-600">Sélectionnez la carte cadeau de votre choix</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-pink-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Payez en ligne</h3>
              <p className="text-gray-600">Paiement sécurisé par carte bancaire</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-amber-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Recevez par email</h3>
              <p className="text-gray-600">Carte cadeau envoyée immédiatement</p>
            </div>
          </div>

          <div className="mt-12 bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
            <p className="text-sm text-purple-900">
              <strong>📌 Bon à savoir :</strong> Les cartes cadeaux sont valables 6 mois à partir de la date d'achat.
              Le montant peut être utilisé en une ou plusieurs fois selon vos envies.
            </p>
          </div>
        </div>
      </section>

      {/* Modal de formulaire */}
      {showForm && selectedCard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-light text-purple-900 mb-2">
                  {currentStep === 1 ? 'Informations' : 'Paiement'} - "{selectedCard.name}"
                </h2>
                <div className={`inline-block bg-gradient-to-r ${selectedCard.gradient} text-white px-4 py-1 rounded-full font-semibold`}>
                  {selectedCard.value}€
                </div>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setSelectedCard(null);
                  setCurrentStep(1);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Indicateur d'étapes */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-purple-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                    1
                  </div>
                  <span className="text-sm font-medium">Informations</span>
                </div>
                <div className="w-12 h-0.5 bg-gray-300"></div>
                <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-purple-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium">Paiement</span>
                </div>
              </div>
            </div>

            {/* ÉTAPE 1 : Informations */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {/* Informations de l'acheteur */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Vos informations
                  </h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      required
                      placeholder="Votre nom complet"
                      value={formData.senderName}
                      onChange={(e) => setFormData({ ...formData, senderName: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                    <input
                      type="email"
                      required
                      placeholder="Votre email"
                      value={formData.senderEmail}
                      onChange={(e) => setFormData({ ...formData, senderEmail: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                    <input
                      type="tel"
                      required
                      placeholder="Votre téléphone"
                      value={formData.senderPhone}
                      onChange={(e) => setFormData({ ...formData, senderPhone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Informations du destinataire */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Destinataire de la carte cadeau
                  </h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      required
                      placeholder="Nom du destinataire"
                      value={formData.recipientName}
                      onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                    <input
                      type="email"
                      required
                      placeholder="Email du destinataire"
                      value={formData.recipientEmail}
                      onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Message personnel */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Message personnel (optionnel)
                  </h3>
                  <textarea
                    placeholder="Ajoutez un message personnalisé pour accompagner votre cadeau..."
                    value={formData.personalMessage}
                    onChange={(e) => setFormData({ ...formData, personalMessage: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none resize-none"
                  />
                </div>

                {/* Récapitulatif */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-purple-900 mb-3">Récapitulatif :</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Carte cadeau : <strong>{selectedCard.name}</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Valeur : <strong>{selectedCard.value}€</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Validité : <strong>6 mois</strong></span>
                    </div>
                  </div>
                </div>

                {/* Boutons */}
                <div className="flex gap-4">
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleContinueToPayment}
                    className={`flex-1 bg-gradient-to-r ${selectedCard.gradient} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity`}
                  >
                    Continuer vers le paiement
                  </button>
                </div>
              </div>
            )}

            {/* ÉTAPE 2 : Paiement */}
            {currentStep === 2 && (
              <Elements stripe={stripePromise}>
                <GiftCardPaymentForm
                  selectedCard={selectedCard}
                  formData={formData}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handleCancel}
                />
              </Elements>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCardSystem;